/**
 * Return-path validation (trimmed from web/src/lib/redirect.ts).
 *
 * The frontend must never perform an open redirect to an external domain.
 * Return paths preserved through sign-in (`?redirect=/actions`) must be
 * same-origin application paths only.
 */

/**
 * A safe return path: absolute path on the current origin, rooted at `/`, no
 * protocol/host. Returns `null` for anything external or malformed.
 */
export function sanitizeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  // Allow the bare origin only.
  if (raw === '/') return '/';

  // Must start with a single slash (no protocol-relative //evil.com).
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // Backslash-based protocol-relative bypass.
  if (raw.startsWith('/\\')) return null;

  try {
    // Parse as a URL relative to a placeholder origin; reject anything that
    // resolves to an absolute URL with a foreign host.
    const resolved = new URL(raw, 'https://admin.local');
    if (resolved.host !== 'admin.local') return null;
    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    // Reject javascript:/data: schemes smuggled via search/hash by re-parsing.
    if (/(javascript|data|vbscript):/i.test(path)) return null;
    // Cap length to avoid abuse.
    if (path.length > 1024) return null;
    return path;
  } catch {
    return null;
  }
}
